import React, { useState, useEffect, useRef } from 'react';
import api from '../api';

function ModelTester({ baseUrl, apiKey, onModelsTested }) {
  const [discovering, setDiscovering] = useState(false);
  const [testing, setTesting] = useState(false);
  const [models, setModels] = useState([]);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);
  const [expandedModel, setExpandedModel] = useState(null);
  const pollInterval = useRef(null);

  const startPolling = (sessionId) => {
    if (pollInterval.current) clearInterval(pollInterval.current);
    pollInterval.current = setInterval(async () => {
      try {
        const response = await fetch('/api/models/progress/' + sessionId, {
          headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') },
        });
        const data = await response.json();
        if (data.status === 'completed' || data.status === 'error') {
          clearInterval(pollInterval.current);
          pollInterval.current = null;
          setTesting(false);
          setProgress(null);
          if (data.status === 'completed') {
            setModels(data.results);
            if (onModelsTested) onModelsTested(data.results);
          } else {
            setError(data.error || 'Testing failed');
          }
        } else {
          setProgress(data);
          if (data.results && data.results.length > 0) {
            setModels(data.results);
          }
        }
      } catch (err) {
        clearInterval(pollInterval.current);
        pollInterval.current = null;
        setTesting(false);
        setError('Failed to fetch progress: ' + err.message);
      }
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (pollInterval.current) clearInterval(pollInterval.current);
    };
  }, []);

  const handleDiscover = async () => {
    setError(null);
    setDiscovering(true);
    setModels([]);
    setProgress(null);
    try {
      const data = await api.discoverModels(baseUrl, apiKey);
      setModels(data.models.map(m => ({
        ...m, status: 'unknown', tokens: null, error: null, responseTime: null, providerResponse: null, excluded: false,
      })));
    } catch (err) {
      setError(err.message);
    } finally {
      setDiscovering(false);
    }
  };

  const toggleExclude = (modelId) => {
    setModels(prev => prev.map(m =>
      m.id === modelId && m.status === 'unknown'
        ? { ...m, excluded: !m.excluded }
        : m
    ));
  };

  const excludeAll = () => {
    setModels(prev => prev.map(m => ({
      ...m,
      excluded: m.status === 'unknown' ? true : m.excluded,
    })));
  };

  const includeAll = () => {
    setModels(prev => prev.map(m => ({
      ...m,
      excluded: m.status === 'unknown' ? false : m.excluded,
    })));
  };

  const handleTestAll = async () => {
    setError(null);
    setTesting(true);
    setProgress(null);
    try {
      // Only test models that are NOT excluded and not already tested
      const toTest = models.filter(m => m.status === 'unknown' && !m.excluded);
      if (toTest.length === 0) {
        setError('No models selected for testing. Use the include/exclude toggle to pick models.');
        setTesting(false);
        return;
      }
      const modelIds = toTest.map(m => m.id);
      // Mark them as 'pending' immediately so UI shows they're queued
      const pendingIds = new Set(modelIds);
      setModels(prev => prev.map(m => pendingIds.has(m.id) ? { ...m, status: 'pending' } : m));
      const data = await api.testAllModels(baseUrl, apiKey, modelIds);
      startPolling(data.sessionId);
    } catch (err) {
      setError(err.message);
      setTesting(false);
    }
  };

  const handleTestSingle = async (modelId) => {
    setError(null);
    setModels(prev => prev.map(m => m.id === modelId ? { ...m, status: 'testing', excluded: false } : m));
    try {
      const result = await api.testSingleModel(baseUrl, apiKey, modelId);
      setModels(prev => prev.map(m =>
        m.id === modelId ? { ...m, ...result, status: result.success ? 'active' : 'failed' } : m
      ));
    } catch (err) {
      setModels(prev => prev.map(m =>
        m.id === modelId ? { ...m, status: 'failed', error: err.message } : m
      ));
    }
  };

  const activeCount = models.filter(m => m.status === 'active').length;
  const failedCount = models.filter(m => m.status === 'failed').length;
  const testingCount = models.filter(m => m.status === 'testing').length;
  const excludedCount = models.filter(m => m.excluded && m.status === 'unknown').length;
  const toTestCount = models.filter(m => m.status === 'unknown' && !m.excluded).length;
  const progressPercent = progress ? Math.round((progress.completed / progress.total) * 100) : 0;

  const toggleExpand = (modelId) => {
    setExpandedModel(expandedModel === modelId ? null : modelId);
  };

  const formatResponse = (response) => {
    if (!response) return 'No response';
    return JSON.stringify(response, null, 2);
  };

  const formatContext = (len) => {
    if (!len) return null;
    if (len >= 1000000) return (len / 1000000).toFixed(2).replace(/\.00$/, '') + 'M';
    if (len >= 1000) return Math.round(len / 1000) + 'K';
    return String(len);
  };

  const formatPrice = (pricing) => {
    if (!pricing) return null;
    if (typeof pricing === 'string') return '$' + parseFloat(pricing).toFixed(5);
    if (pricing.prompt !== undefined) {
      const p = parseFloat(pricing.prompt);
      return p > 0 ? '$' + p.toFixed(5) + '/K in' : 'free';
    }
    return null;
  };

  return (
    <div className="model-tester">
      <div className="model-tester-actions">
        <button onClick={handleDiscover} disabled={discovering || testing} className="btn btn-outline">
          {discovering ? 'Discovering...' : 'Discover Models'}
        </button>
        <button onClick={handleTestAll} disabled={discovering || testing || toTestCount === 0} className="btn btn-primary">
          {testing ? 'Testing... (' + progress?.completed + '/' + progress?.total + ')' : 'Test ' + toTestCount + ' Models'}
        </button>
        <button onClick={excludeAll} disabled={discovering || testing} className="btn btn-outline">
          Exclude All
        </button>
        <button onClick={includeAll} disabled={discovering || testing} className="btn btn-outline">
          Include All
        </button>
      </div>

      {testing && progress && (
        <div className="test-progress">
          <div className="progress-header">
            <span className="progress-text">
              Testing: {progress.completed}/{progress.total} completed
              {progress.active > 0 && ' (' + progress.active + ' threads active)'}
            </span>
            <span className="progress-percent">{progressPercent}%</span>
          </div>
          <div className="progress-bar-container">
            <div className="progress-bar-fill" style={{ width: progressPercent + '%' }} />
          </div>
          <div className="progress-stats">
            <span className="badge badge-success">{activeCount} active</span>
            <span className="badge badge-error">{failedCount} failed</span>
            {testingCount > 0 && (
              <span className="badge" style={{ background: 'rgba(245, 158, 11, 0.2)', color: 'var(--warning)' }}>
                {testingCount} testing
              </span>
            )}
          </div>
        </div>
      )}

      {error && <div className="alert alert-error" style={{ marginTop: '1rem' }}>{error}</div>}

      {models.length > 0 && (
        <div className="model-test-results">
          {!testing && (
            <div className="model-test-summary">
              <span className="badge badge-success">{activeCount} active</span>
              <span className="badge badge-error">{failedCount} failed</span>
              <span className="badge badge-muted">{models.length} total</span>
              {excludedCount > 0 && (
                <span className="badge badge-muted" style={{ opacity: 0.7 }}>{excludedCount} excluded</span>
              )}
              {toTestCount > 0 && (
                <span className="badge" style={{ background: 'rgba(99, 102, 241, 0.2)', color: 'var(--primary)' }}>
                  {toTestCount} to test
                </span>
              )}
            </div>
          )}
          <div className="model-list">
            {models.map(model => (
              <div key={model.id} className={'model-item status-' + model.status + (model.excluded ? ' model-excluded' : '')}>
                <div className="model-exclude-toggle" onClick={(e) => { e.stopPropagation(); }}>
                  {model.status === 'unknown' && (
                    <label className="exclude-checkbox" title={model.excluded ? 'Click to include' : 'Click to exclude'}>
                      <input
                        type="checkbox"
                        checked={!model.excluded}
                        onChange={() => toggleExclude(model.id)}
                        disabled={testing}
                      />
                      <span>Test</span>
                    </label>
                  )}
                </div>
                <div className="model-main" onClick={() => toggleExpand(model.id)}>
                  <div className="model-info">
                    <span className="model-name">{model.id}</span>
                    {model.owned_by && <span className="model-owner">by {model.owned_by}</span>}
                  </div>
                  <div className="model-meta">
                    {model.name && <span className="model-desc">{model.name}</span>}
                    {formatContext(model.context_length) && (
                      <span className="model-context" title="Context size">
                        {'\u{1F4BE}'} {formatContext(model.context_length)}
                      </span>
                    )}
                    {formatPrice(model.pricing) && (
                      <span className="model-price" title="Pricing ($/1K tokens)">
                        {'\u{1F4B0}'} {formatPrice(model.pricing)}
                      </span>
                    )}
                    {model.responseTime && <span className="model-response-time">{model.responseTime}ms</span>}
                    {model.tokens && <span className="model-tokens">{model.tokens} tokens</span>}
                  </div>
                  <div className="model-status-indicator">
                    {model.excluded && model.status === 'unknown' && (
                      <span className="status-badge status-excluded">Excluded</span>
                    )}
                    {model.status === 'active' && <span className="status-badge status-active">Active</span>}
                    {model.status === 'failed' && <span className="status-badge status-failed">Failed</span>}
                    {model.status === 'rate_limited' && <span className="status-badge status-rate-limited">Rate Limited</span>}
                    {model.status === 'testing' && <span className="status-badge status-testing">Testing...</span>}
                    {model.status === 'pending' && <span className="status-badge status-pending">Pending...</span>}
                    {model.status === 'unknown' && !model.excluded && !testing && (
                      <button onClick={(e) => { e.stopPropagation(); handleTestSingle(model.id); }} disabled={testing} className="btn btn-sm btn-outline">Test</button>
                    )}
                    {model.status === 'unknown' && !model.excluded && testing && (
                      <span className="status-badge status-pending">Pending...</span>
                    )}
                    {model.providerResponse && (
                      <span className="expand-hint">{expandedModel === model.id ? '\u25B2' : '\u25BC'}</span>
                    )}
                  </div>
                </div>
                {expandedModel === model.id && model.providerResponse && (
                  <div className="model-response">
                    <div className="response-label">Provider Response:</div>
                    <pre className="response-content">{formatResponse(model.providerResponse)}</pre>
                  </div>
                )}
                {expandedModel === model.id && model.error && !model.providerResponse && (
                  <div className="model-response">
                    <div className="response-label">Error:</div>
                    <pre className="response-content error">{model.error}</pre>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ModelTester;
